import { type Component, createEffect, createSignal, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { Portal } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import { usePresence } from '~/hooks/usePresence'
import type { ModalBodyPropsType, ModalFooterPropsType, ModalHeaderPropsType, ModalPropsType, ModalSizeType } from '~/types/Modal'
import { lockScroll, unlockScroll } from '~/utils/dom'
import { getGsap, MOTION } from '~/utils/gsap'
import Button from './Button'

const MOBILE_BREAKPOINT = 640

// Max-height per size for mobile bottom sheet (cap at 90dvh)
const MOBILE_SIZE_HEIGHT: Record<ModalSizeType, string> = {
  xs: 'max-h-[40dvh]',
  sm: 'max-h-[55dvh]',
  md: 'max-h-[70dvh]',
  lg: 'max-h-[80dvh]',
  xl: 'max-h-[88dvh]',
  full: 'max-h-[90dvh]',
  fullscreen: 'max-h-[100dvh]'
}

const Modal: Component<ModalPropsType> & {
  Header: Component<ModalHeaderPropsType>
  Body: Component<ModalBodyPropsType>
  Footer: Component<ModalFooterPropsType>
} = props => {
  const [local, others] = splitProps(props, ['isOpen', 'onClose', 'children', 'size', 'closable', 'fullscreen', 'closeOnBackdropClick', 'closeOnEscape', 'class', 'canMove'])

  const [isMobile, setIsMobile] = createSignal(false)

  let panelRef: HTMLDivElement | undefined
  let backdropRef: HTMLDivElement | undefined

  // ── Arrastre (canMove) ─────────────────────────────────────────────────────
  // Se aplica el desplazamiento a un wrapper para no chocar con el transform
  // que GSAP usa en la animación de entrada/salida sobre el panel.
  const [dragOffset, setDragOffset] = createSignal({ x: 0, y: 0 })
  const [dragging, setDragging] = createSignal(false)
  let dragState: {
    pointerId: number
    startX: number
    startY: number
    originX: number
    originY: number
  } | null = null

  // El offset se reinicia solo cuando la animación de salida termina y el modal
  // se desmonta (onExited). Si se hiciera al empezar a cerrar, el panel "saltaría"
  // de vuelta al centro mientras se anima la salida.

  const handleDragStart = (e: PointerEvent) => {
    if (!local.canMove) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // Solo se arrastra desde el header: se ignora si el puntero baja en otra zona
    // o sobre un elemento interactivo (botón de cerrar, enlaces, inputs…).
    const target = e.target as Element | null
    if (!target?.closest?.('[data-modal-drag-handle]')) return
    if (target.closest('button, a, input, textarea, select, [contenteditable="true"]')) return
    dragState = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: dragOffset().x,
      originY: dragOffset().y
    }
    try {
      ;(e.currentTarget as Element | null)?.setPointerCapture?.(e.pointerId)
    } catch {
      // Algunos navegadores/eventos sintéticos no tienen un puntero activo que capturar.
    }
    setDragging(true)
  }

  const handleDragMove = (e: PointerEvent) => {
    if (!dragState || !panelRef) return
    const dx = e.clientX - dragState.startX
    const dy = e.clientY - dragState.startY

    const proposedX = dragState.originX + dx
    const proposedY = dragState.originY + dy

    // Limita el arrastre para que el modal no se salga de la ventana: el panel
    // debe quedar siempre con un margen mínimo visible dentro del viewport.
    const rect = panelRef.getBoundingClientRect()
    const currentX = dragOffset().x
    const currentY = dragOffset().y
    const vw = window.innerWidth
    const vh = window.innerHeight
    const margin = 16

    const minX = margin - rect.left + currentX
    const maxX = vw - margin - rect.left - rect.width + currentX
    const minY = margin - rect.top + currentY
    const maxY = vh - margin - rect.top - rect.height + currentY

    const x = Math.min(Math.max(proposedX, Math.min(minX, maxX)), Math.max(minX, maxX))
    const y = Math.min(Math.max(proposedY, Math.min(minY, maxY)), Math.max(minY, maxY))

    setDragOffset({ x, y })
  }

  const handleDragEnd = () => {
    dragState = null
    setDragging(false)
  }

  const isFullscreen = (): boolean => local.fullscreen || local.size === 'fullscreen'
  const isSheet = () => isMobile() || isFullscreen()

  const buildEnter = (panel: HTMLElement) => {
    const g = getGsap()
    if (!g) return
    const tl = g.timeline()
    if (backdropRef) tl.fromTo(backdropRef, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'power2.out' }, 0)
    if (isSheet()) {
      tl.fromTo(panel, { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: MOTION.sheet.enter, ease: MOTION.sheet.ease }, 0)
    } else {
      tl.fromTo(panel, { scale: 0.95, opacity: 0 }, { scale: 1, opacity: 1, duration: MOTION.enter, ease: 'power2.out' }, 0)
    }
    return tl
  }

  const buildExit = (panel: HTMLElement) => {
    const g = getGsap()
    if (!g) return
    const tl = g.timeline()
    if (backdropRef) tl.to(backdropRef, { opacity: 0, duration: 0.18, ease: 'power2.in' }, 0)
    if (isSheet()) {
      tl.to(panel, { yPercent: 100, opacity: 0, duration: MOTION.sheet.exit, ease: 'power3.in' }, 0)
    } else {
      tl.to(panel, { scale: 0.95, opacity: 0, duration: MOTION.exit, ease: 'power2.in' }, 0)
    }
    return tl
  }

  const presence = usePresence({
    open: () => local.isOpen,
    enter: el => buildEnter(el),
    exit: el => buildExit(el),
    onEntered: () => {
      // Asegura visibilidad bajo movimiento reducido (cuando no se ejecuta el timeline).
      if (panelRef) panelRef.style.opacity = ''
      if (backdropRef) backdropRef.style.opacity = ''
    },
    onExited: () => {
      // Se reinicia la posición tras desmontar, para que al volver a abrir parta del centro.
      setDragOffset({ x: 0, y: 0 })
      unlockScroll()
    }
  })

  // Bloqueo de scroll al abrir (independiente de la animación, respeta reduced-motion).
  createEffect(() => {
    if (local.isOpen) lockScroll()
  })

  onMount(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    setIsMobile(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (local.isOpen && local.closeOnEscape !== false && e.key === 'Escape') local.onClose()
    }
    window.addEventListener('keydown', handleKeyDown)

    onCleanup(() => {
      mq.removeEventListener('change', handler)
      window.removeEventListener('keydown', handleKeyDown)
      if (presence.mounted()) unlockScroll()
    })
  })

  const handleBackdropClick = (e: MouseEvent) => {
    if (local.closeOnBackdropClick === false) return
    if (e.target === e.currentTarget) local.onClose()
  }

  const getSizeClasses = (): string => {
    const size = local.size ?? 'md'
    if (isFullscreen()) return 'w-full h-dvh max-w-none max-h-none'
    if (isMobile()) return MOBILE_SIZE_HEIGHT[size] ?? 'max-h-[70dvh]'

    const sizeMap: Record<ModalSizeType, string> = {
      xs: 'w-80',
      sm: 'w-96',
      md: 'w-[28rem]',
      lg: 'w-[36rem]',
      xl: 'w-[44rem]',
      full: 'w-full',
      fullscreen: 'w-full h-full max-w-none max-h-none'
    }
    return sizeMap[size] || 'max-w-md'
  }

  const modalClasses = () => {
    const userClasses = local.class ?? ''
    if (isFullscreen()) {
      return twMerge('relative z-10 flex flex-col transform overflow-hidden bg-surface', userClasses, 'w-full h-dvh rounded-none max-w-none max-h-none')
    }
    if (isMobile()) {
      return twMerge('relative z-10 flex flex-col transform overflow-hidden bg-surface', userClasses, 'rounded-t-2xl rounded-b-none shadow-2xl w-full', getSizeClasses())
    }
    return twMerge('relative z-10 flex flex-col transform overflow-hidden bg-surface', 'rounded-lg shadow-xl max-h-[85vh]', getSizeClasses(), userClasses)
  }

  return (
    <Show when={presence.mounted()}>
      <Portal>
        {/* Capas: la aplicación llega hasta z-50 (cabeceras sticky, menús). Los
            overlays van por encima: modal 1000 < toast 1100 < tooltip 1200. */}
        <div class="fixed inset-0 z-[1000] overflow-hidden">
          {/* Backdrop — opacidad controlada por GSAP */}
          <div ref={backdropRef} class="absolute inset-0 bg-black/50" style={{ opacity: 0 }} aria-hidden="true" />

          <div
            class={twMerge('relative flex h-full w-full', isFullscreen() ? 'items-end justify-center' : isMobile() ? 'items-end justify-center' : 'items-center justify-center p-4')}
            onClick={handleBackdropClick}
          >
            {/* Wrapper de arrastre: recibe el translate y los eventos pointer.
                Vive fuera del panel para no tocar el transform que anima GSAP. */}
            <div
              class={twMerge(
                'relative',
                isMobile() || isFullscreen() ? 'w-full' : '',
                local.canMove ? '[&_[data-modal-drag-handle]]:cursor-grab [&_[data-modal-drag-handle]:active]:cursor-grabbing' : ''
              )}
              style={{
                transform: local.canMove ? `translate3d(${dragOffset().x}px, ${dragOffset().y}px, 0)` : undefined,
                'touch-action': local.canMove ? (dragging() ? 'none' : 'pan-y') : undefined
              }}
              onPointerDown={handleDragStart}
              onPointerMove={handleDragMove}
              onPointerUp={handleDragEnd}
              onPointerCancel={handleDragEnd}
            >
              {/* Modal panel — transform/opacity controlados por GSAP */}
              <div
                ref={el => {
                  panelRef = el
                  presence.setRef(el)
                }}
                class={modalClasses()}
                style={{ opacity: 0 }}
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                {...others}
              >
                <Show when={local.closable}>
                  <Button
                    size="xs"
                    variant="text"
                    onClick={local.onClose}
                    icon="material-symbols-light:close"
                    class={twMerge('absolute top-3 right-3 z-20', 'text-foreground-subtle hover:text-foreground')}
                  />
                </Show>

                <div class="flex flex-col w-full flex-1 min-h-0">{local.children}</div>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  )
}

Modal.Header = props => {
  const [local, others] = splitProps(props, ['class'])
  const headerClasses = () => twMerge('sticky top-0 z-10 bg-surface border-b border-border w-full shrink-0 px-6 py-4', local.class)
  return (
    <div {...others} data-modal-drag-handle class={headerClasses()}>
      {props.children}
    </div>
  )
}

Modal.Body = props => {
  const [local, others] = splitProps(props, ['class'])
  const bodyClasses = () => twMerge('flex-1 overflow-y-auto overscroll-contain w-full min-h-0 px-6 py-4', local.class)
  return (
    <div {...others} class={bodyClasses()}>
      {props.children}
    </div>
  )
}

Modal.Footer = props => {
  const [local, others] = splitProps(props, ['class'])
  const footerClasses = () => twMerge('sticky bottom-0 z-10 bg-surface border-t border-border w-full shrink-0 px-6 py-4', local.class)
  return (
    <div {...others} class={footerClasses()}>
      {props.children}
    </div>
  )
}

export default Modal
